import { cn } from './lib/utils.js';

export type DimeStepperStep<Id extends string | number = string | number> = {
  id: Id;
  title: string;
  badge?: string;
  disabled?: boolean;
};

export interface DimeStepperProps<Id extends string | number> {
  steps: readonly DimeStepperStep<Id>[];
  currentStepId: Id;
  className?: string;
  itemClassName?: string;
  onStepSelect?: (stepId: Id) => void;
}

export function DimeStepper<Id extends string | number>({
  steps,
  currentStepId,
  className,
  itemClassName,
  onStepSelect,
}: Readonly<DimeStepperProps<Id>>) {
  const currentIndex = steps.findIndex((step) => step.id === currentStepId);
  return (
    <ol className={cn('mb-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-2', className)}>
      {steps.map((step, index) => {
        const isActive = index === currentIndex;
        const isComplete = index < currentIndex;
        const body = (
          <>
            <span className="font-medium">Step {index + 1}</span>
            <span className="mt-0.5 block">{step.title}</span>
          </>
        );
        return (
          <li
            key={step.id}
            aria-current={isActive ? 'step' : undefined}
            className={cn(
              'relative min-w-[10rem] flex-1 rounded-md border text-sm',
              isActive && 'border-primary bg-primary/5 text-foreground',
              !isActive && isComplete && 'border-border text-muted-foreground',
              !isActive && !isComplete && 'border-border text-muted-foreground/70',
              itemClassName,
            )}
          >
            {onStepSelect ? (
              <button
                type="button"
                className="w-full px-3 py-2.5 text-left disabled:cursor-not-allowed disabled:opacity-50"
                disabled={step.disabled}
                onClick={() => onStepSelect(step.id)}
              >
                {body}
              </button>
            ) : (
              <div className="px-3 py-2.5">{body}</div>
            )}
            {step.badge && (
              <span className="pointer-events-none absolute top-2 right-2 rounded-full bg-amber-100 px-2 text-xs font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                {step.badge}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
